import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import GeneralConfirmModal from 'components/general-confirm-modal';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import UPSwitch from 'components/switch';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';
import {
  getPolicyImpactAction,
  getTimeRegistrationPolicyAction,
  savePolicyUnitAction,
} from 'store/actions/time-registration-policy-actions';
import {
  GENERAL__CLOSE,
  GENERAL__SAVE_BUTTON,
  TIME_REGISTRATION_POLICY__COLUMN_DEFAULT,
  TIME_REGISTRATION_POLICY__COLUMN_PERMISSION,
  TIME_REGISTRATION_POLICY__CONCURRENT_CHANGE,
  TIME_REGISTRATION_POLICY__PANEL_GROUP,
  TIME_REGISTRATION_POLICY__PERMISSION_LOCKED_TEXT,
  TIME_REGISTRATION_POLICY__PERMISSION_OVERRULABLE_TEXT,
  TIME_REGISTRATION_POLICY__PERMISSION_TOGGLE,
  TIME_REGISTRATION_POLICY__RELOAD,
  TIME_REGISTRATION_POLICY__SAVE_FAILED,
  TIME_REGISTRATION_POLICY__SAVE_TIMEOUT,
} from 'translations/constants';
import {
  FieldErrors,
  PolicyImpactResult,
  PolicySaveResult,
  ProposedUnitState,
  UnitState,
  UnitValue,
  UnitValueField,
} from 'types/time-registration-policy/ITimeRegistrationPolicy';
import {
  buildConfirmation,
  ConfirmationLine,
  hasUnitChanged,
  hasValueChanged,
  isSwitchableKind,
  pickUnitFields,
  toProposed,
  validateUnitValue,
} from './helpers';
import PolicyUnitFields from './PolicyUnitFields';
import { PanelErrorRow, PanelExplanation, PanelFields, PermissionHelp, SwitchLabel, SwitchRow } from './styles';
import { getUnitDefinition, POLICY_SECTIONS } from './units';

interface PolicyUnitPanelProps {
  state: UnitState;
  onClose: () => void;
  onSaved: (name: string) => void;
}

interface PendingConfirmation {
  titleKey: string;
  lines: ConfirmationLine[];
  proposed: ProposedUnitState;
}

const PolicyUnitPanel = ({ state, onClose, onSaved }: PolicyUnitPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const def = getUnitDefinition(state.unit);
  const sectionTitleKey = POLICY_SECTIONS.find(section => section.section === def.section)?.titleKey;

  const [draft, setDraft] = useState<UnitValue>(pickUnitFields(def, state.value));
  const [overrulable, setOverrulable] = useState<boolean>(state.overrulable);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);

  useEffect(() => {
    setDraft(pickUnitFields(def, state.value));
    setOverrulable(state.overrulable);
    setErrors({});
  }, [def, state]);

  const onFieldChange = (patch: Partial<UnitValue>) => {
    setDraft(prev => ({ ...prev, ...patch }));
    setErrors(prev => {
      const next = { ...prev };
      (Object.keys(patch) as UnitValueField[]).forEach(field => delete next[field]);
      return next;
    });
    setAlert(null);
  };

  const buildProposed = (): ProposedUnitState => {
    const keepStoredSubFields = isSwitchableKind(def.kind) && !draft.enabled;
    const value = keepStoredSubFields ? { ...pickUnitFields(def, state.value), enabled: false } : draft;
    return { value, overrulable };
  };

  const showFailure = (result: PolicyImpactResult | PolicySaveResult) => {
    switch (result.status) {
      case 'conflict':
        setConflict(true);
        setAlert(t(TIME_REGISTRATION_POLICY__CONCURRENT_CHANGE));
        break;
      case 'timeout':
        setAlert(t(TIME_REGISTRATION_POLICY__SAVE_TIMEOUT));
        break;
      case 'invalid':
        setErrors(result.fieldErrors);
        break;
      default:
        setAlert(t(TIME_REGISTRATION_POLICY__SAVE_FAILED));
    }
  };

  const requestSave = async (proposed: ProposedUnitState) => {
    setSaving(true);
    setAlert(null);
    const result = await dispatch(getPolicyImpactAction({ unit: state.unit, proposed })).unwrap();
    setSaving(false);
    if (result.status !== 'ok') {
      showFailure(result);
      return;
    }
    const { titleKey, lines } = buildConfirmation(
      def,
      result.impact,
      hasValueChanged(def, state.value, proposed.value),
    );
    setConfirmation({ titleKey, lines, proposed });
  };

  const onSave = () => {
    if (saving) return;
    const nextErrors = validateUnitValue(def, draft);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    const proposed = buildProposed();
    if (!hasUnitChanged(def, state, proposed)) {
      onClose();
      return;
    }
    requestSave(proposed);
  };

  const confirmSave = async () => {
    if (!confirmation || saving) return;
    const { proposed } = confirmation;
    setConfirmation(null);
    setSaving(true);
    const result = await dispatch(
      savePolicyUnitAction({ unit: state.unit, expected: toProposed(state), proposed }),
    ).unwrap();
    setSaving(false);
    if (result.status === 'success') {
      onSaved(t(def.nameKey));
      onClose();
      return;
    }
    showFailure(result);
  };

  const reloadAfterConflict = async () => {
    await dispatch(getTimeRegistrationPolicyAction());
    setConflict(false);
    setAlert(null);
  };

  const close = () => {
    if (!saving) onClose();
  };

  return (
    <>
      <UPSidePanel
        open
        title={t(def.nameKey)}
        subtitle={t(TIME_REGISTRATION_POLICY__PANEL_GROUP, { section: t(sectionTitleKey) })}
        onClose={close}
        testId="tr-panel"
        footer={
          <>
            <UPButton text={t(GENERAL__SAVE_BUTTON)} onClick={onSave} disabled={saving} testId="tr-panel-save" />
            <UPButton text={t(GENERAL__CLOSE)} outlined onClick={close} testId="tr-panel-close" />
          </>
        }
      >
        {alert && (
          <PanelErrorRow data-testid="tr-panel-alert">
            <ErrorBar message={alert} />
            {conflict && (
              <UPButton
                text={t(TIME_REGISTRATION_POLICY__RELOAD)}
                outlined
                onClick={reloadAfterConflict}
                testId="tr-panel-reload"
              />
            )}
          </PanelErrorRow>
        )}
        <UPSidePanelSection title={t(TIME_REGISTRATION_POLICY__COLUMN_DEFAULT)} testId="tr-panel-default">
          <PanelExplanation data-testid="tr-panel-explanation">{t(def.descKey)}</PanelExplanation>
          <PanelFields>
            <PolicyUnitFields def={def} value={draft} errors={errors} onChange={onFieldChange} />
          </PanelFields>
        </UPSidePanelSection>
        <UPSidePanelSection title={t(TIME_REGISTRATION_POLICY__COLUMN_PERMISSION)} testId="tr-panel-permission">
          <SwitchRow>
            <SwitchLabel>{t(TIME_REGISTRATION_POLICY__PERMISSION_TOGGLE)}</SwitchLabel>
            <UPSwitch
              checked={overrulable}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOverrulable(event.target.checked);
                setAlert(null);
              }}
              testId="tr-field-overrulable"
            />
          </SwitchRow>
          <PermissionHelp>
            {t(
              overrulable
                ? TIME_REGISTRATION_POLICY__PERMISSION_OVERRULABLE_TEXT
                : TIME_REGISTRATION_POLICY__PERMISSION_LOCKED_TEXT,
            )}
          </PermissionHelp>
        </UPSidePanelSection>
      </UPSidePanel>
      {confirmation && (
        <GeneralConfirmModal
          open
          content={[t(confirmation.titleKey), ...confirmation.lines.map(line => t(line.key, line.params))].join('\n\n')}
          onAction={confirmSave}
          onClose={() => setConfirmation(null)}
          actionDisabled={saving}
          confirmTestId="tr-confirm-accept"
          cancelTestId="tr-confirm-cancel"
        />
      )}
    </>
  );
};

export default PolicyUnitPanel;
